/**
 * @file AuthQuestionMarquee.tsx
 * @brief Login brand panel: three drifting rows of questions you can ask your
 *        own meetings, as on the website's home page.
 *
 * @details
 * DES (01-10-2026), on the canvas "hello" network: "its too heavy agar koi
 * user app open kar kay bhool gaya to yeh chalta rahay ga", then, pointing at
 * the website strip: "i think we use this like we have in our webiste, speak
 * every language kay ooper, mobile per abhi jo hai wo rehnay daytay hain".
 *
 * Cost: none on the main thread. Each row is a CSS `transform` animation, which
 * the browser runs on the compositor (GPU); there is no JavaScript loop. The
 * row content is doubled and moves by half its width, so it loops seamlessly.
 * - After 60 s with no mouse, keyboard or touch, the rows pause (a class on
 *   the root sets `animation-play-state: paused`); any input resumes them. So a
 *   desktop app left open on the login screen goes still.
 * - Hidden tabs and minimised windows stop compositor animations on their own.
 * - `prefers-reduced-motion`: the rows do not move (index.css).
 *
 * Copy and shape follow WEBSITE/src/pages/index.astro (QROWS) and
 * WEBSITE/src/styles/global.css (.qmq / .qchip), in the panel's light colours.
 * Decorative: aria-hidden, nothing here is a control.
 *
 * @author DES
 * @date 01-10-2026
 */

import { useIdle } from "@/shared/hooks/useIdle";
import { LanguageFlag } from "@/components/ui/LanguageFlag";
import { cn } from "@/shared/theme/cn";

/**
 * Every pill in a different language (DES 01-10-2026: "do that so every pill
 * is in different langauage"), so the strip says "One table. Many tongues" before
 * the headline does. Same questions as the website, translated; `lang` tags
 * each for the browser's font fallback and screen readers, `dir="auto"` lets
 * Urdu and Arabic run right to left inside their pill.
 */
type Q = { text: string; lang: string; locale: string };
const QROWS: Q[][] = [
  [
    { lang: "en", locale: "en-GB", text: "Who owns the migration?" },
    { lang: "ur", locale: "ur-PK", text: "قیمت پر ہم نے کیا طے کیا تھا؟" },
    { lang: "es", locale: "es-ES", text: "¿Cuándo se movió la fecha límite?" },
    { lang: "fr", locale: "fr-FR", text: "Qui a soulevé le problème de sécurité ?" },
    { lang: "de", locale: "de-DE", text: "Was hat die Beschaffung blockiert?" },
    { lang: "ar", locale: "ar-SA", text: "ما الإجراءات التي لا تزال مفتوحة؟" },
    { lang: "hi", locale: "hi-IN", text: "साइट विज़िट से क्या निकला?" },
  ],
  [
    { lang: "pt", locale: "pt-PT", text: "O que prometemos ao fornecedor?" },
    { lang: "it", locale: "it-IT", text: "Chi si è impegnato per martedì?" },
    { lang: "tr", locale: "tr-TR", text: "Veri yerleşimi hakkında ne dedik?" },
    { lang: "pl", locale: "pl-PL", text: "Które spotkania wspominały o audycie?" },
    { lang: "zh", locale: "zh-CN", text: "当时谁在场？" },
    { lang: "ja", locale: "ja-JP", text: "前回のレビュー以降、何が変わりましたか？" },
    { lang: "ko", locale: "ko-KR", text: "고객이 실제로 요청한 것은 무엇인가요?" },
  ],
  [
    { lang: "ro", locale: "ro-RO", text: "Cine a fost de acord cu schimbarea?" },
    { lang: "pa", locale: "pa-IN", text: "ਅਸੀਂ ਆਖਰੀ ਵਾਰ ਬਜਟ ਬਾਰੇ ਕਦੋਂ ਗੱਲ ਕੀਤੀ ਸੀ?" },
    { lang: "nl", locale: "nl-NL", text: "Wat is er besloten over het contract?" },
    { lang: "ru", locale: "ru-RU", text: "Кто связывается с арендодателем?" },
    { lang: "bn", locale: "bn-BD", text: "ভাড়াটে কী জানিয়েছেন?" },
    { lang: "sv", locale: "sv-SE", text: "Vilket beslut hör den här offerten till?" },
    { lang: "el", locale: "el-GR", text: "Τι περιμένει ακόμα το νομικό τμήμα;" },
  ],
];

/** Seconds per loop and start offset per row, as on the website. */
const DURATION = [62, 74, 56];
const DELAY = [0, 21, 9];
const IDLE_MS = 60_000;

export function AuthQuestionMarquee({ className }: { className?: string }) {
  const idle = useIdle(IDLE_MS);

  return (
    <div
      aria-hidden
      className={cn("auth-qmq flex flex-col gap-3 opacity-70", idle && "auth-qmq-idle", className)}
    >
      {QROWS.map((row, r) => {
        // Rows 1 and 3 move left, row 2 moves right; the flag sits at the
        // leading end of each pill.
        const left = r !== 1;
        return (
          <div key={r} className="overflow-hidden">
            <div
              className={cn("auth-qtrack flex w-max gap-3", !left && "auth-qtrack-rev")}
              style={{ animationDuration: `${DURATION[r]}s`, animationDelay: `-${DELAY[r]}s` }}
            >
              {[0, 1].map((copy) =>
                row.map((q) => (
                  <span
                    key={`${copy}-${q.lang}`}
                    className={cn(
                      // Grey and dim, a backdrop rather than content (DES 01-10-2026: "inko grey karo aur dim karo").
                      "auth-qchip flex h-10 shrink-0 items-center gap-3 whitespace-nowrap rounded-full text-[15px] text-muted-foreground",
                      left ? "flex-row-reverse pr-5" : "pl-5",
                      copy === 1 && "auth-qdup",
                    )}
                  >
                    <span lang={q.lang} dir="auto">
                      {q.text}
                    </span>
                    {/* The language's flag where the arrow was (DES 01-10-2026:
                        "arrows ki jaga flags lagaoo"). */}
                    <LanguageFlag
                      language={{ code: q.lang, locale: q.locale }}
                      className="h-10 w-10 border-0"
                    />
                  </span>
                )),
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
